import { useAuth } from "@elit-hair/elit-tools";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Badge,
    Button,
    EmptyState,
    Icon,
    Input,
    Kbd,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Skeleton,
    Spinner,
    Textarea,
} from "@elit-hair/elit-ui";
import { createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import axios from "axios";
import { DateTime } from "luxon";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    TbArrowLeft,
    TbArrowRight,
    TbDeviceFloppy,
    TbHistory,
    TbListDetails,
    TbLock,
    TbPlayerPlayFilled,
    TbSparkles,
    TbUsers,
} from "react-icons/tb";
import { toast } from "sonner";

import { Link as AppLink } from "@/components/link";
import { PageActions } from "@/components/page-actions";
import { ADMIN_SURFACE_CLASS, AdminSurface } from "@/components/ui/admin-surface";
import { PERMISSIONS } from "@/constants/permissions";
import { useCallsPaginated } from "@/hooks/queries/call.queries";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { useUserScopedLocalStorage } from "@/hooks/use-user-scoped-local-storage";
import { cn } from "@/lib/utils";
import { useGetCall } from "@/queries/calls/calls.queries";
import { useDimensionDefinitions } from "@/queries/dimension-definition.queries";
import { useGetTranscriptsByCallId } from "@/queries/transcript/transcript.queries";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { AnalyserAccessDialog } from "./components/analyser-access-dialog";
import { AnalyserLogsSheet } from "./components/analyser-logs-sheet";
import { ANALYSER_STATUS_STYLES, AvatarStack } from "./components/analyser-ui";
import { AnalyserVersionsDialog } from "./components/analyser-versions-dialog";
import { AgentInspector } from "./components/editor/agent-inspector";
import {
    ANALYSER_EDITOR_STEPS,
    type AnalyserEditorStep,
    AnalyserEditorStepper,
} from "./components/editor/analyser-editor-stepper";
import { getLeadLabel } from "./components/editor/call-preview";
import { FilterInspector } from "./components/editor/filter-inspector";
import { OutputInspector } from "./components/editor/output-inspector";
import { TestRunCard } from "./components/editor/test-run-card";
import { TriggerInspector } from "./components/editor/trigger-inspector";
import { NEW_ANALYSER_ID, createNewAnalyser } from "./constants/analyser.constants";
import { useAnalyserUsers } from "./hooks/use-analyser-users";
import {
    useCallAnalyser,
    useCallAnalyserModels,
    useCallAnalysers,
    useCreateCallAnalyser,
    useDryRunCallAnalyser,
    useRestoreCallAnalyserVersion,
    useSaveCallAnalyserVersion,
    useSetCallAnalyserEditors,
} from "./queries/call-analyser.queries";
import type { AnalyserDraft, AnalyserDryRunResult, AnalyserStatus, ManagedAnalyser } from "./types/analyser.types";
import type { WorkflowRunStatus, WorkflowTranscriptState } from "./types/workflow.types";
import { getAnalyserModelLabel } from "./utils/analyser-models";
import { describeVersionChanges } from "./utils/analyser-stats";
import { isResultDimensionComplete } from "./utils/workflow-agent";
import { buildCallContext, buildWorkflowFields, isTranscriptReady } from "./utils/workflow-fields";
import { evaluateFilter } from "./utils/workflow-filters";

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_ANALYSER)({
    component: CallAnalyserEditorPage,
    staticData: {
        config: {
            title: "developer_call_analyser_title",
            breadcrumb: { title: "developer_call_analyser_title" },
        },
    },
});

const toDraft = (analyser: ManagedAnalyser): AnalyserDraft => ({
    description: analyser.description,
    status: analyser.status,
    filter: structuredClone(analyser.versions[0].filter),
    agent: structuredClone(analyser.versions[0].agent),
});

const IDLE_STATUS: Record<AnalyserEditorStep, WorkflowRunStatus> = {
    trigger: "idle",
    filter: "idle",
    analyser: "idle",
    result: "idle",
};

const STEP_DELAY_MS = 350;

const SAVE_SHORTCUT = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘S" : "Ctrl S";

/** Step statuses for a finished test, in the order they light up. */
const getResultSteps = (result: AnalyserDryRunResult): Partial<Record<AnalyserEditorStep, WorkflowRunStatus>>[] => {
    if (result.stoppedAt === "trigger") {
        return [{ trigger: "failed", filter: "skipped", analyser: "skipped", result: "skipped" }];
    }
    if (result.stoppedAt === "filter") {
        return [{ trigger: "success" }, { filter: "failed", analyser: "skipped", result: "skipped" }];
    }

    const failed = result.errors.length > 0;
    return [
        { trigger: "success" },
        { filter: "success" },
        failed ? { analyser: "failed", result: "skipped" } : { analyser: "success" },
        ...(failed ? [] : [{ result: "success" as const }]),
    ];
};

const getErrorMessage = (error: unknown, fallback: string) =>
    axios.isAxiosError(error) && typeof error.response?.data?.message === "string"
        ? error.response.data.message
        : fallback;

function CallAnalyserEditorPage() {
    const { analyserId } = Route.useParams();
    const navigate = useNavigate();
    const { userData } = useAuth();
    const isNew = analyserId === NEW_ANALYSER_ID;
    const permissionCodes: string[] = userData?.permissionCodes ?? [];
    const canCreate = permissionCodes.includes(PERMISSIONS.CALL_ANALYSER_CREATE);

    const { data: loadedAnalyser, isLoading: isLoadingAnalyser } = useCallAnalyser(isNew ? undefined : analyserId);
    const { data: models = [], isLoading: isLoadingModels } = useCallAnalyserModels();
    const defaultModel = models[0]?.id ?? "";
    const newAnalyser = useMemo(
        () => (isNew ? createNewAnalyser(userData?.id ?? "", canCreate, defaultModel) : null),
        [canCreate, defaultModel, isNew, userData?.id]
    );
    const analyser = isNew ? newAnalyser : (loadedAnalyser ?? null);
    const isEditor = analyser?.canEdit ?? false;
    // The list holds the 7-day run stats for the Logs sheet.
    const { data: analysers } = useCallAnalysers();
    const stats = analysers?.find((item) => item.id === analyser?.id)?.stats;

    const saveVersion = useSaveCallAnalyserVersion();
    const createAnalyser = useCreateCallAnalyser();
    const restoreVersion = useRestoreCallAnalyserVersion();
    const setEditors = useSetCallAnalyserEditors();
    const testAnalyser = useDryRunCallAnalyser();

    const [draft, setDraft] = useState<AnalyserDraft | null>(analyser ? toDraft(analyser) : null);
    const [note, setNote] = useState("");
    const [step, setStep] = useState<AnalyserEditorStep>("trigger");
    // Remembers the sample call across visits so tests can resume from the same call.
    const [storedSampleCallId, setStoredSampleCallId] = useUserScopedLocalStorage<string | null>(
        "analyser-sample-call-id",
        null
    );
    const sampleCallId = storedSampleCallId ?? undefined;
    const setSampleCallId = useCallback(
        (callId: string | undefined) => {
            setStoredSampleCallId(callId ?? null);
        },
        [setStoredSampleCallId]
    );
    const [runStatus, setRunStatus] = useState(IDLE_STATUS);
    const [runResult, setRunResult] = useState<AnalyserDryRunResult | null>(null);
    const [dialog, setDialog] = useState<"versions" | "logs" | "access" | null>(null);
    const timeoutsRef = useRef<number[]>([]);

    const { users, currentUserId } = useAnalyserUsers(
        analyser
            ? [analyser.ownerId, ...analyser.editorIds, ...analyser.versions.map((version) => version.authorId)]
            : []
    );
    const { data: teamsData } = useTeams();
    const teams = useMemo(() => teamsData?.data ?? [], [teamsData?.data]);
    const fields = useMemo(() => buildWorkflowFields(teams), [teams]);
    // Sample calls: the latest 25, optionally of one agent.
    const [sampleAgentId, setSampleAgentId] = useState<string | null>(null);
    const {
        data: callsData,
        isLoading: isLoadingCalls,
        isFetching: isFetchingCalls,
        refetch: refetchCalls,
    } = useCallsPaginated({ page: 1, limit: 25, userId: sampleAgentId ?? undefined });
    const calls = callsData.data;
    const { data: definitionsData } = useDimensionDefinitions();
    const definitions = useMemo(
        () => [...(definitionsData?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
        [definitionsData?.data]
    );
    // The chosen sample call from the recent list, or loaded by id when it isn't among them.
    const callFromList = sampleCallId ? calls.find((call) => call.id === sampleCallId) : undefined;
    const {
        data: callById,
        isLoading: isLoadingCallById,
        isError: isCallByIdError,
        isFetching: isFetchingCallById,
    } = useGetCall(sampleCallId && !callFromList ? sampleCallId : undefined);
    // The chosen sample call, else the newest one with a recording (transcripts are made from it).
    const sampleCall = sampleCallId
        ? (callFromList ?? callById)
        : (calls.find((call) => call.callRecordingUrl) ?? calls[0]);
    // The analyser starts once the transcript is ready, so the sample is shown with its real transcript.
    const { data: transcripts, isLoading: isLoadingTranscript } = useGetTranscriptsByCallId(sampleCall?.id);
    const transcript = useMemo(
        () =>
            [...(transcripts ?? [])]
                .filter(isTranscriptReady)
                .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ?? null,
        [transcripts]
    );
    const transcriptState: WorkflowTranscriptState = isLoadingTranscript ? "loading" : transcript ? "ready" : "missing";
    const context = useMemo(
        () => (sampleCall ? buildCallContext(sampleCall, teams, isLoadingTranscript ? undefined : transcript) : null),
        [isLoadingTranscript, sampleCall, teams, transcript]
    );

    const clearRun = useCallback(() => {
        timeoutsRef.current.forEach((timeout) => window.clearTimeout(timeout));
        timeoutsRef.current = [];
        setRunStatus(IDLE_STATUS);
    }, []);

    useEffect(() => clearRun, [clearRun]);

    // Start from the current version when the analyser opens or a new version is saved or restored.
    const currentVersion = analyser?.versions[0].version;
    useEffect(() => {
        if (analyser) {
            setDraft(toDraft(analyser));
            setNote("");
        }
        // Only a new version resets the draft; the analyser object also changes on unrelated edits (e.g. access).
    }, [analyser?.id, currentVersion]);

    // New analysers get the first model from Anthropic once the list has loaded.
    useEffect(() => {
        if (!isNew || !defaultModel) {
            return;
        }
        setDraft((current) =>
            current && !current.agent.model ? { ...current, agent: { ...current.agent, model: defaultModel } } : current
        );
    }, [defaultModel, isNew]);

    // Leaving with unsaved changes asks first; ⌘S / Ctrl+S saves.
    const isDirty = Boolean(analyser?.canEdit && draft && JSON.stringify(draft) !== JSON.stringify(toDraft(analyser)));
    const skipBlockRef = useRef(false);
    const blocker = useBlocker({
        shouldBlockFn: () => isDirty && !skipBlockRef.current,
        enableBeforeUnload: () => isDirty,
        withResolver: true,
    });
    const saveShortcutRef = useRef<(() => void) | null>(null);
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
                event.preventDefault();
                saveShortcutRef.current?.();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    if (!isNew && isLoadingAnalyser) {
        return (
            <AdminSurface className="flex justify-center p-16">
                <Spinner />
            </AdminSurface>
        );
    }

    if (!analyser || !draft || (isNew && !canCreate)) {
        return (
            <AdminSurface className="p-8">
                <EmptyState
                    variant="simple"
                    icon={<TbSparkles size={40} className="text-muted-foreground" />}
                    title={isNew ? "You can't create analysers" : "Analyser not found"}
                />
                <div className="mt-4 flex justify-center">
                    <Button asChild variant="outline">
                        <AppLink to={APP_ROUTES_TO.DEVELOPER_ANALYSERS}>
                            <Icon icon={TbArrowLeft} />
                            Back to call analysers
                        </AppLink>
                    </Button>
                </div>
            </AdminSurface>
        );
    }

    const hasChanges = isNew || JSON.stringify(draft) !== JSON.stringify(toDraft(analyser));
    const nextVersion = analyser.versions[0].version + 1;
    const author = users.find((user) => user.id === analyser.versions[0].authorId);
    const editors = analyser.editorIds
        .map((id) => users.find((user) => user.id === id))
        .filter((user): user is NonNullable<typeof user> => Boolean(user));
    const filterPassed = context ? evaluateFilter(draft.filter, context).passed : null;
    // Only result dimensions with a definition and a prompt are part of the analysis.
    const completeDimensions = draft.agent.dimensions.filter((dimension) =>
        isResultDimensionComplete(dimension, definitions)
    );
    const incompleteDimensions = draft.agent.dimensions.length - completeDimensions.length;
    const modelLabel = getAnalyserModelLabel(models, draft.agent.model);
    const isRunning = testAnalyser.isPending || Object.values(runStatus).includes("running");
    const isSaving = saveVersion.isPending || createAnalyser.isPending;
    const stepIndex = ANALYSER_EDITOR_STEPS.findIndex((item) => item.id === step);
    const previousStep = ANALYSER_EDITOR_STEPS[stepIndex - 1];
    const nextStep = ANALYSER_EDITOR_STEPS[stepIndex + 1];
    // What blocks saving, in the order to fix it.
    const saveBlocker = !draft.agent.name.trim()
        ? "Give the analyser a name"
        : !draft.agent.model
          ? "Select a model"
          : !draft.agent.prompt.trim()
            ? "Write the prompt"
            : completeDimensions.length === 0
              ? "Add at least one result dimension"
              : incompleteDimensions > 0
                ? `Complete or remove ${incompleteDimensions} result dimension${incompleteDimensions === 1 ? "" : "s"}`
                : null;

    // What the save would change, for the save bar and as the default version note.
    const savedDraft = toDraft(analyser);
    const changes = isNew
        ? []
        : [
              ...(draft.status !== savedDraft.status
                  ? [
                        `Status ${ANALYSER_STATUS_STYLES[savedDraft.status].label} → ${ANALYSER_STATUS_STYLES[draft.status].label}`,
                    ]
                  : []),
              ...(draft.description !== savedDraft.description ? ["Description changed"] : []),
              ...(draft.agent.name !== savedDraft.agent.name ? ["Renamed"] : []),
              ...describeVersionChanges(draft, savedDraft).filter((change) => change !== "No changes to the setup"),
          ];

    const updateDraft = (update: Partial<AnalyserDraft>) => {
        clearRun();
        setRunResult(null);
        setDraft((current) => (current ? { ...current, ...update } : current));
    };

    // Tests the unsaved draft on the sample call with the real model; nothing is saved.
    const runTest = () => {
        if (!sampleCall) {
            return;
        }
        if (completeDimensions.length === 0) {
            toast.error("Add at least one complete result dimension to test the analyser");
            setStep("analyser");
            return;
        }

        clearRun();
        setRunResult(null);
        setRunStatus({ ...IDLE_STATUS, trigger: "running", filter: "running", analyser: "running" });

        testAnalyser.mutate(
            { callId: sampleCall.id, draft: { ...draft, agent: { ...draft.agent, dimensions: completeDimensions } } },
            {
                onSuccess: (result) => {
                    setRunStatus(IDLE_STATUS);
                    getResultSteps(result).forEach((update, index) => {
                        timeoutsRef.current.push(
                            window.setTimeout(
                                () => setRunStatus((current) => ({ ...current, ...update })),
                                index * STEP_DELAY_MS
                            )
                        );
                    });
                    timeoutsRef.current.push(
                        window.setTimeout(
                            () => {
                                setRunResult(result);
                                setStep(
                                    result.stoppedAt === "trigger"
                                        ? "trigger"
                                        : result.stoppedAt === "filter"
                                          ? "filter"
                                          : "result"
                                );
                            },
                            getResultSteps(result).length * STEP_DELAY_MS
                        )
                    );
                },
                onError: (error) => {
                    clearRun();
                    toast.error(getErrorMessage(error, "The test run failed"));
                },
            }
        );
    };

    const save = () => {
        if (isNew) {
            createAnalyser.mutate(
                { ...draft, note: note.trim() || undefined },
                {
                    onSuccess: (created) => {
                        toast.success("Analyser created");
                        skipBlockRef.current = true;
                        navigate({ to: APP_ROUTES_TO.DEVELOPER_ANALYSER, params: { analyserId: created.id } });
                    },
                    onError: (error) => toast.error(getErrorMessage(error, "Couldn't create the analyser")),
                }
            );
            return;
        }

        saveVersion.mutate(
            {
                id: analyser.id,
                draft,
                note: note.trim() || changes.join(", "),
                baseVersion: analyser.versions[0].version,
            },
            {
                onSuccess: (saved) => toast.success(`Saved version ${saved.versions[0].version}`),
                onError: (error) =>
                    toast.error(
                        axios.isAxiosError(error) && error.response?.status === 409
                            ? "Someone saved a newer version meanwhile. Reload the page to see it before saving."
                            : getErrorMessage(error, "Couldn't save the analyser")
                    ),
            }
        );
    };

    saveShortcutRef.current = isEditor && hasChanges && !saveBlocker && !isSaving ? save : null;

    const summaries: Record<AnalyserEditorStep, ReactNode> = {
        trigger: (
            <>
                <span className="block font-medium text-foreground/80">Transcript ready</span>
                <span
                    className={cn(
                        "block truncate",
                        transcriptState === "missing" && "text-amber-600 dark:text-amber-400"
                    )}
                >
                    {!context
                        ? "Pick a sample call"
                        : transcriptState === "missing"
                          ? `${getLeadLabel(context)} has no transcript`
                          : `Sample: ${getLeadLabel(context)}`}
                </span>
            </>
        ),
        filter: (
            <>
                <span className="block font-medium text-foreground/80">
                    {draft.filter.conditions.length
                        ? `Match ${draft.filter.match} of ${draft.filter.conditions.length}`
                        : "Every call passes"}
                </span>
                {filterPassed !== null ? (
                    <span
                        className={cn(
                            "block",
                            filterPassed ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"
                        )}
                    >
                        {filterPassed ? "Sample call passes" : "Sample call is filtered out"}
                    </span>
                ) : null}
            </>
        ),
        analyser: (
            <>
                <span className="block truncate font-medium text-foreground/80">{modelLabel}</span>
                <span className={cn("block", incompleteDimensions > 0 && "text-amber-600 dark:text-amber-400")}>
                    {completeDimensions.length} result dimension{completeDimensions.length === 1 ? "" : "s"}
                    {incompleteDimensions > 0 ? ` · ${incompleteDimensions} incomplete` : ""}
                </span>
            </>
        ),
        result: (
            <>
                <span className="block font-medium text-foreground/80">Saved to the call</span>
                <span className="block">
                    {testAnalyser.isPending
                        ? "Claude is analysing…"
                        : runResult
                          ? runResult.stoppedAt || runResult.errors.length
                              ? "Nothing would be saved"
                              : "Test run finished"
                          : "Run a test to see it"}
                </span>
            </>
        ),
    };

    return (
        <div className="space-y-5 pb-4">
            <PageActions>
                <Button asChild variant="ghost">
                    <AppLink to={APP_ROUTES_TO.DEVELOPER_ANALYSERS}>
                        <Icon icon={TbArrowLeft} />
                        All analysers
                    </AppLink>
                </Button>
                {isNew ? null : (
                    <>
                        <Button variant="outline" onClick={() => setDialog("versions")}>
                            <Icon icon={TbHistory} />
                            Versions
                        </Button>
                        <Button variant="outline" onClick={() => setDialog("logs")}>
                            <Icon icon={TbListDetails} />
                            Logs
                        </Button>
                    </>
                )}
            </PageActions>

            <section className={cn(ADMIN_SURFACE_CLASS, "overflow-hidden")}>
                <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-start">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon icon={TbSparkles} size={20} />
                    </span>
                    <div className="min-w-0 flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                            {/* The analyser's name (stored as the agent name); edited right here in the header. */}
                            <h1 className="min-w-0 max-w-full flex-1 sm:flex-none">
                                <input
                                    value={draft.agent.name}
                                    disabled={!isEditor}
                                    placeholder="Analyser name"
                                    aria-label="Analyser name"
                                    size={Math.max(draft.agent.name.length, 12)}
                                    onChange={(event) =>
                                        updateDraft({ agent: { ...draft.agent, name: event.target.value } })
                                    }
                                    className="-ml-1.5 w-full max-w-full rounded-lg bg-transparent px-1.5 py-0.5 text-xl font-semibold tracking-tight outline-none transition-colors placeholder:text-muted-foreground hover:bg-muted/60 focus:bg-background focus:ring-2 focus:ring-ring disabled:cursor-default disabled:hover:bg-transparent"
                                />
                            </h1>
                            <Badge variant="outline" label={isNew ? "New" : `v${analyser.versions[0].version}`} />
                            {hasChanges && !isNew ? <Badge variant="warning" label="Unsaved changes" /> : null}
                        </div>
                        <Textarea
                            value={draft.description}
                            disabled={!isEditor}
                            rows={2}
                            placeholder="What does this analyser measure?"
                            onChange={(event) => updateDraft({ description: event.target.value })}
                            className="resize-none border-transparent bg-transparent px-0 text-sm text-muted-foreground shadow-none focus-visible:border-input focus-visible:bg-background focus-visible:px-3 disabled:cursor-default disabled:opacity-100"
                        />
                        <p className="text-xs text-muted-foreground">
                            {isNew
                                ? "Not saved yet"
                                : `Last saved by ${author?.id === currentUserId ? "you" : (author?.name ?? "someone")} ${DateTime.fromISO(analyser.versions[0].createdAt).toRelative()}`}
                        </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-3">
                        <Select
                            value={draft.status}
                            disabled={!isEditor}
                            onValueChange={(status) => updateDraft({ status: status as AnalyserStatus })}
                        >
                            <SelectTrigger className="h-9 w-32">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {(Object.keys(ANALYSER_STATUS_STYLES) as AnalyserStatus[]).map((status) => (
                                    <SelectItem key={status} value={status}>
                                        <span className="inline-flex items-center gap-2">
                                            <span
                                                className={cn(
                                                    "size-2 rounded-full",
                                                    ANALYSER_STATUS_STYLES[status].dot
                                                )}
                                            />
                                            {ANALYSER_STATUS_STYLES[status].label}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <button
                            type="button"
                            disabled={isNew}
                            onClick={() => setDialog("access")}
                            className="flex items-center gap-2 rounded-lg p-1 transition-colors hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent"
                            title={
                                isNew ? "You'll be the owner" : isEditor ? "Manage who can edit" : "See who can edit"
                            }
                        >
                            <AvatarStack users={editors} currentUserId={currentUserId} />
                            <Icon icon={TbUsers} size={14} className="text-muted-foreground" />
                        </button>
                    </div>
                </div>
                {isEditor ? null : (
                    <p className="flex items-center gap-2 border-t border-border/50 bg-muted/20 px-5 py-2.5 text-xs text-muted-foreground">
                        <Icon icon={TbLock} size={14} />
                        You can view and test this analyser, but only its editors can change it.
                    </p>
                )}
            </section>

            <div className="grid gap-4 lg:grid-cols-[minmax(260px,300px)_minmax(0,1fr)] lg:items-start">
                <aside className="space-y-3 lg:sticky lg:top-0">
                    <AnalyserEditorStepper
                        selected={step}
                        summaries={summaries}
                        status={runStatus}
                        onSelect={setStep}
                    />
                    <TestRunCard
                        sampleLabel={context ? getLeadLabel(context) : null}
                        transcriptState={transcriptState}
                        isRunning={isRunning}
                        result={runResult}
                        onRun={runTest}
                        onChangeSample={() => setStep("trigger")}
                    />
                </aside>

                <section className={cn(ADMIN_SURFACE_CLASS, "min-w-0 overflow-hidden")}>
                    <div className="border-b border-border/50 px-5 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            Step {stepIndex + 1}
                        </p>
                        <p className="text-sm font-semibold">{ANALYSER_EDITOR_STEPS[stepIndex].label}</p>
                        <p className="text-xs text-muted-foreground">{ANALYSER_EDITOR_STEPS[stepIndex].description}</p>
                    </div>

                    <div className="p-5">
                        <div key={step}>
                            {step === "trigger" ? (
                                <TriggerInspector
                                    trigger={{ event: "transcript_ready", sampleCallId: sampleCall?.id }}
                                    transcriptState={transcriptState}
                                    calls={calls}
                                    teams={teams}
                                    isLoadingCalls={isLoadingCalls}
                                    isRefreshingCalls={isFetchingCalls && !isLoadingCalls}
                                    isLoadingCallById={isLoadingCallById || isFetchingCallById}
                                    isCallByIdError={isCallByIdError}
                                    agentId={sampleAgentId}
                                    onAgentChange={(agentId) => {
                                        setSampleAgentId(agentId);
                                    }}
                                    onRefreshCalls={() => void refetchCalls()}
                                    sampleCall={sampleCall}
                                    context={context}
                                    fields={fields}
                                    onChange={(trigger) => {
                                        clearRun();
                                        setRunResult(null);
                                        setSampleCallId(trigger.sampleCallId);
                                    }}
                                />
                            ) : null}
                            {/* A disabled fieldset locks every input, select and button inside for view-only users. */}
                            {step === "filter" ? (
                                <fieldset disabled={!isEditor} className="min-w-0">
                                    <FilterInspector
                                        filter={draft.filter}
                                        fields={fields}
                                        context={context}
                                        onChange={(filter) => updateDraft({ filter })}
                                    />
                                </fieldset>
                            ) : null}
                            {step === "analyser" ? (
                                <fieldset disabled={!isEditor} className="min-w-0">
                                    <AgentInspector
                                        agent={draft.agent}
                                        fields={fields}
                                        definitions={definitions}
                                        models={models}
                                        modelsLoading={isLoadingModels}
                                        readOnly={!isEditor}
                                        onChange={(agent) => updateDraft({ agent })}
                                    />
                                </fieldset>
                            ) : null}
                            {step === "result" ? (
                                testAnalyser.isPending ? (
                                    <div className="space-y-2" aria-busy>
                                        <p className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Spinner size="sm" />
                                            Claude is analysing the sample call…
                                        </p>
                                        {completeDimensions.map((dimension) => (
                                            <Skeleton key={dimension.id} className="h-16 rounded-xl" />
                                        ))}
                                    </div>
                                ) : (
                                    <OutputInspector dimensions={completeDimensions} result={runResult} />
                                )
                            ) : null}
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 border-t border-border/50 bg-muted/20 px-5 py-3">
                        {previousStep ? (
                            <Button variant="ghost" onClick={() => setStep(previousStep.id)}>
                                <Icon icon={TbArrowLeft} />
                                {previousStep.label}
                            </Button>
                        ) : (
                            <span />
                        )}
                        {nextStep ? (
                            <Button variant="outline" onClick={() => setStep(nextStep.id)}>
                                {nextStep.label}
                                <Icon icon={TbArrowRight} />
                            </Button>
                        ) : (
                            <Button variant="outline" onClick={runTest} disabled={!sampleCall || isRunning}>
                                <Icon icon={TbPlayerPlayFilled} />
                                Run again
                            </Button>
                        )}
                    </div>
                </section>
            </div>

            {isEditor && hasChanges ? (
                <div className="sticky bottom-0 z-20 space-y-2 rounded-2xl border border-border/60 bg-card/95 p-3 shadow-md backdrop-blur-sm">
                    {changes.length > 0 ? (
                        <p className="truncate text-xs text-muted-foreground" title={changes.join("\n")}>
                            <span className="font-medium text-foreground">
                                {changes.length} change{changes.length === 1 ? "" : "s"}:
                            </span>{" "}
                            {changes.join(" · ")}
                        </p>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-2">
                        <Input
                            value={note}
                            onChange={(event) => setNote(event.target.value)}
                            placeholder={
                                isNew
                                    ? "Note for the first version (optional)"
                                    : changes.length
                                      ? `Version note (default: ${changes.join(", ")})`
                                      : "Version note (optional)"
                            }
                            className="h-9 min-w-56 flex-1"
                        />
                        {saveBlocker ? (
                            <span className="text-xs text-amber-700 dark:text-amber-400">{saveBlocker}</span>
                        ) : null}
                        {isNew ? null : (
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    clearRun();
                                    setRunResult(null);
                                    setDraft(toDraft(analyser));
                                    setNote("");
                                }}
                            >
                                Discard
                            </Button>
                        )}
                        <Button disabled={Boolean(saveBlocker) || isSaving} onClick={save}>
                            <Icon icon={TbDeviceFloppy} />
                            {isSaving ? "Saving…" : isNew ? "Create analyser" : `Save v${nextVersion}`}
                            <Kbd className="ml-1 hidden bg-primary-foreground/15 text-primary-foreground sm:inline-flex">
                                {SAVE_SHORTCUT}
                            </Kbd>
                        </Button>
                    </div>
                </div>
            ) : null}

            <AlertDialog open={blocker.status === "blocked"} onOpenChange={(open) => !open && blocker.reset?.()}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Your changes to this analyser aren&apos;t saved yet and will be lost.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => blocker.reset?.()}>Keep editing</AlertDialogCancel>
                        <AlertDialogAction onClick={() => blocker.proceed?.()}>Leave</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {isNew ? null : (
                <>
                    <AnalyserVersionsDialog
                        analyser={dialog === "versions" ? analyser : null}
                        users={users}
                        models={models}
                        currentUserId={currentUserId}
                        canEdit={isEditor}
                        onOpenChange={(open) => !open && setDialog(null)}
                        onRestore={(version) =>
                            restoreVersion.mutate(
                                { id: analyser.id, version },
                                {
                                    onSuccess: (restored) =>
                                        toast.success(
                                            `Version ${version} restored as version ${restored.versions[0].version}`
                                        ),
                                    onError: (error) =>
                                        toast.error(getErrorMessage(error, "Couldn't restore the version")),
                                }
                            )
                        }
                    />
                    <AnalyserLogsSheet
                        analyser={dialog === "logs" ? analyser : null}
                        stats={stats}
                        onOpenChange={(open) => !open && setDialog(null)}
                    />
                    <AnalyserAccessDialog
                        analyser={dialog === "access" ? analyser : null}
                        users={users}
                        currentUserId={currentUserId}
                        isSaving={setEditors.isPending}
                        onOpenChange={(open) => !open && setDialog(null)}
                        onSave={(editorIds) =>
                            setEditors.mutate(
                                { id: analyser.id, editorIds },
                                {
                                    onSuccess: () => {
                                        toast.success("Access updated");
                                        setDialog(null);
                                    },
                                    onError: (error) =>
                                        toast.error(getErrorMessage(error, "Couldn't update the access")),
                                }
                            )
                        }
                    />
                </>
            )}
        </div>
    );
}
